import { useTranslation } from "react-i18next";
import type { WorkerDto, ProductDto, OperationDto } from "@normax-performance/shared-types";
import { SearchCombobox, type ComboboxOption } from "../../../shared-ui/primitives";
import { formatCodeAndName } from "../../../lib/format";

export interface ReportEntityPickerProps {
  kind: "worker" | "product" | "operation";
  workers: WorkerDto[];
  products: ProductDto[];
  operations: OperationDto[];
  onSelect: (id: string) => void;
}

/** Reuses the exact `SearchCombobox` + `formatCodeAndName` + `rankSearchMatches`
 * stack `EntryHeader.tsx` (Daily Entry) already established — no second
 * search engine (task spec §28). */
export function ReportEntityPicker({ kind, workers, products, operations, onSelect }: ReportEntityPickerProps) {
  const { t } = useTranslation("reports");

  const options: ComboboxOption[] =
    kind === "worker"
      ? workers.map((w) => ({ id: w.id, label: formatCodeAndName(w.employeeCode, `${w.lastName} ${w.firstName}`), businessCode: w.employeeCode }))
      : kind === "product"
        ? products.map((p) => ({ id: p.id, label: formatCodeAndName(p.code, p.canonicalName), businessCode: p.code }))
        : operations.map((o) => ({ id: o.id, label: formatCodeAndName(o.code, o.canonicalName), businessCode: o.code }));

  return (
    <SearchCombobox
      id="nx-report-entity-picker"
      label={t(`entityPicker.${kind}`)}
      items={options}
      selectedKey={null}
      onSelectionChange={(key) => {
        if (key) onSelect(key);
      }}
      placeholder={t("entityPicker.placeholder")}
      emptyMessage={t("entityPicker.empty")}
      required
    />
  );
}
